import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Library,
  NotebookPen,
  Repeat,
  ShieldCheck,
  Sparkles,
  SpellCheck,
} from 'lucide-react';
import { APP_NAME } from '@en3/shared';
import { contentApi, type RecentArticle } from '@/api/content.api';
import { queryKeys } from '@/api/client';
import { useAuthStatus, useMe } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Badge } from '@/components/ui/card';
import { LoadingBlock } from '@/components/common/States';

/**
 * 首页。
 *
 * 这一版的重点是**把"计划"换成"事实"**：上一版每个能力卡片上挂着「阶段 2–7」的
 * 计划标签，还写着"批次 5 正在搭建"。那种信息对用户没有价值（他关心的是"我现在能做什么"），
 * 而且会随开发进度迅速过期。现在每张卡片直接链到真实路由并标明「已上线」。
 *
 * 数据侧有三件事：
 * 1. 未登录：不请求任何用户数据，只给一句"登录后可记录学习数据"的引导；
 *    立刻发请求再显示 401 是最常见的做法，也是最没必要的做法。
 * 2. 登录后：`/api/me/recent` 拿最近读过的文章（继续学习），`/api/me` 拿统计。
 *    两者分开缓存，因为"最近读了什么"和"总体统计"的刷新时机完全不同。
 * 3. 合规提示保留在页脚上方，并且是可点的 —— 它必须能被找到，而不是一句装饰性声明。
 */

const FEATURES = [
  {
    to: ROUTES.textbooks,
    icon: BookOpen,
    title: '教材与文章',
    desc: '按教材 / 单元 / 文章组织内容。平台只内置公开目录，正文由有权限的教师或用户导入。',
    cta: '浏览目录',
  },
  {
    to: ROUTES.textbooks,
    icon: SpellCheck,
    title: '点词查义',
    desc: '音标、词性、中英释义、例句与发音，并结合上下文给出「文中义」。进入任意文章即可点词。',
    cta: '进入文章点词',
  },
  {
    to: ROUTES.wordbook,
    icon: NotebookPen,
    title: '生词本',
    desc: '查过或标记过的词自动收集，可加笔记与标签，并按间隔重复安排复习。',
    cta: '打开生词本',
  },
  {
    to: ROUTES.review,
    icon: Repeat,
    title: '间隔复习',
    desc: '按 SM-2 变体调度到期卡片，用挖空句回译与自评巩固记忆。',
    cta: '开始复习',
  },
  {
    to: ROUTES.sentences,
    icon: Library,
    title: '长难句解析',
    desc: '识别长难句，给出主干、修饰成分、从句类型、拆解步骤与仿写练习。',
    cta: '解析长难句',
  },
  {
    to: ROUTES.level,
    icon: GraduationCap,
    title: '水平报告',
    desc: '依据查词与复习行为估计 CEFR 等级、词汇量区间、薄弱项与提升路径。',
    cta: '查看水平报告',
  },
];

export function HomePage() {
  const status = useAuthStatus();
  const authenticated = status === 'authenticated';
  const me = useMe();

  /**
   * 继续学习：只在登录后请求。
   *
   * `enabled` 是关键 —— 未登录时请求 /api/me/recent 必然 401，
   * 白白触发一次刷新令牌的尝试，还可能在控制台留下噪声错误。
   */
  const recentQuery = useQuery({
    queryKey: queryKeys.recent,
    queryFn: () => contentApi.recent(3),
    enabled: authenticated,
  });

  const stats = me.data?.stats;
  const recentItems = recentQuery.data?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl px-5 py-10">
      <section>
        <p className="text-xs font-semibold tracking-widest text-primary uppercase">Cloudflare 全栈 · 移动端优先</p>
        {/*
          h1 的文本必须包含「大学英语三」（`APP_NAME` = 「大学英语三辅助学习平台」）。
          e2e/app-shell.spec.ts 直接断言这段文字，而且它必须**在数据请求之外**渲染：
          下面「继续学习」「学习数据」两块随时可能处于加载/错误态，但标题不能跟着消失，
          否则慢环境下测试会不稳定。改文案时请保住这四个字。
        */}
        <h1 className="mt-2 text-3xl font-bold sm:text-4xl">{APP_NAME}</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          面向「大学英语三」的英语辅助学习平台：点词查义、生词本与间隔复习、长难句解析、
          水平评估（CEFR / 词汇量 / 薄弱项）与学习方法推荐。
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to={ROUTES.textbooks}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
          >
            浏览教材目录
          </Link>
          <Link to={ROUTES.copyright} className="rounded-md border px-4 py-2 text-sm hover:bg-accent">
            版权与数据来源
          </Link>
        </div>
      </section>

      {/* 继续学习：放在能力卡片之前。回访用户最想做的第一件事是"接着上次读"，
          而不是重新理解这个平台能做什么。 */}
      {authenticated && (
        <section className="mt-10">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">继续学习</h2>
            <Link to={ROUTES.textbooks} className="text-xs text-primary underline underline-offset-4">
              去教材目录找新文章
            </Link>
          </div>

          <div className="mt-3">
            {recentQuery.isPending ? (
              <LoadingBlock label="正在读取最近阅读记录…" />
            ) : recentQuery.isError ? (
              <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                最近阅读记录加载失败，不影响浏览教材；稍后刷新页面再试。
              </p>
            ) : recentItems.length === 0 ? (
              <p className="rounded-lg border border-dashed px-4 py-4 text-sm text-muted-foreground">
                还没有阅读记录。打开任意一篇文章开始读，进度会自动保存，下次就能从这里一键接着读。
              </p>
            ) : (
              <ul className="space-y-2">
                {recentItems.map((item) => (
                  <RecentItem key={item.article.id} item={item} />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      <section className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ to, icon: Icon, title, desc, cta }) => (
          <Link
            key={title}
            to={to}
            className="group flex flex-col rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary"
          >
            <div className="flex items-center gap-2">
              <Icon aria-hidden className="size-4 text-primary" />
              <h2 className="font-semibold">{title}</h2>
              <Badge tone="success" className="ml-auto">
                <Sparkles aria-hidden className="size-3" />
                已上线
              </Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary">
              {cta}
              <ArrowRight aria-hidden className="size-3.5" />
            </span>
          </Link>
        ))}
      </section>

      {/* 统计：登录后显示真实数据，未登录改成引导。 */}
      <section className="mt-10">
        <h2 className="text-lg font-semibold">学习数据</h2>

        {!authenticated ? (
          <p className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-4 py-4 text-sm text-muted-foreground">
            登录后可记录学习数据：查过的词、待复习的卡片、阅读进度都会保存下来。
            <Link
              to={ROUTES.login}
              className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
            >
              登录 / 注册
            </Link>
          </p>
        ) : me.isPending ? (
          <LoadingBlock label="正在读取学习数据…" />
        ) : me.isError ? (
          <p role="alert" className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            学习数据加载失败，请稍后重试。
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard to={ROUTES.wordbook} label="生词数" value={String(stats?.wordCount ?? 0)} action="去生词本" />
            <StatCard to={ROUTES.review} label="今日待复习" value={String(stats?.dueToday ?? 0)} action="去复习" />
            <StatCard
              to={ROUTES.level}
              label="当前等级"
              value={stats?.currentCefr ?? '未评估'}
              action={stats?.currentCefr ? '看水平报告' : '去做评估'}
            />
          </div>
        )}
      </section>

      <section className="mt-10 flex items-start gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-primary" />
        <p className="leading-relaxed">
          平台不内置受版权保护的教材全文，只收录公开的目录元数据；正文由具备使用授权的教师或用户自行导入并留痕。
          权利人可要求下架，详见{' '}
          <Link to={ROUTES.copyright} className="text-primary underline underline-offset-4">
            版权与数据来源
          </Link>
          。
        </p>
      </section>
    </div>
  );
}

/** 一篇"上次读到"的文章：标题 + 进度条 + 一键继续。 */
function RecentItem({ item }: { item: RecentArticle }) {
  const { article, progress } = item;
  const pct = Math.min(100, Math.max(0, progress.progressPct));

  return (
    <li>
      <Link
        to={ROUTES.reader(article.id)}
        className="block rounded-lg border bg-card p-3 text-card-foreground shadow-sm transition-colors hover:border-primary hover:bg-accent/40"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium break-words">{article.titleZh ?? article.title}</p>
            {article.titleZh && <p className="mt-0.5 text-xs break-words text-muted-foreground">{article.title}</p>}
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-primary">
            继续阅读
            <ArrowRight aria-hidden className="size-3.5" />
          </span>
        </div>

        <div
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          aria-label="阅读进度"
        >
          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          已读 {Math.round(pct)}% · 上次读到第 {progress.lastGlobalIndex + 1} 句
          {progress.finishedAt ? ' · 已读完' : ''}
        </p>
      </Link>
    </li>
  );
}

/** 统计小卡片。整块可点，点击直达对应页面，避免"看得到数字却不知道去哪用"。 */
function StatCard({ to, label, value, action }: { to: string; label: string; value: string; action: string }) {
  return (
    <Link
      to={to}
      className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary"
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      <p className="mt-1 inline-flex items-center gap-1 text-xs text-primary">
        {action}
        <ArrowRight aria-hidden className="size-3.5" />
      </p>
    </Link>
  );
}
